/* cali sticky buy dock (2026-08-20, v4)

   Builds the dock's contents from the page's own buy UI, so the dock can never
   disagree with the page:
   - colour swatches are CLONES of the page's picker labels. A cloned
     <label for="X"> activates input X wherever it lives in the document, so a
     tap runs the theme's own variant logic (price, images, URL, master select).
     Anything the page hides is absent here too.
   - other options (size, ...) become a compact select that clicks the real input.
   - the primary button submits the real form (form="" attribute) in form mode,
     or forwards its click to the React app's button in proxy mode.
   - the page's secondary CTA (e.g. "Design yours") is mirrored when present.
   - the title is hidden while the page's own H1 is on screen, so the customer
     never reads the product name twice.

   Visibility/state is computed SYNCHRONOUSLY from getBoundingClientRect on init
   and on throttled scroll/resize. Never let an async observer decide it. */

(function () {
  'use strict';

  var GA4 = 'G-C7J5W11GYN'; /* same property the language switcher reports to */

  /* Distinguishes a click in the dock from a click on the page's own button,
     so a bug in either place is visible. Params ride on two event names:
     pdp_cta_click {placement, cta, ...} and buy_bar_variant_change.
     NOTE: GA4 is accepters-only since the consent bar went in, so these
     under-count - they are for comparing placements, not absolute volume.
     To use placement/cta in standard reports they must be registered as
     custom dimensions in GA4 admin; they show in Realtime/DebugView already. */
  function track(name, params) {
    if (typeof window.gtag !== 'function') return;
    params = params || {};
    params.send_to = GA4;
    try { window.gtag('event', name, params); } catch (e) {}
  }

  /* The Bag Builder dialog resets the page scroll to ~210px when it closes -
     reproducible from the page's OWN "Design yours" button too, so this is the
     app's behaviour, not the dock's. Restore the position the customer left
     from, and stand down the moment they scroll themselves. */
  function preserveScrollAcrossBuilder() {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var sawDialog = false;
    var started = Date.now();
    /* Only .ui-dialog-* signal an OPEN builder. Do not include .mc-mainDiv:
       it is a permanent container that exists (and is display:flex, 720px
       tall) even with the dialog closed, so treating it as "open" means the
       close is never detected and nothing is ever restored. */
    var isOpen = function () {
      return !!document.querySelector('.ui-dialog-content, .ui-dialog-overlay');
    };
    var poll = setInterval(function () {
      if (isOpen()) { sawDialog = true; return; }
      if (!sawDialog) {
        if (Date.now() - started > 5000) clearInterval(poll); /* never opened */
        return;
      }
      clearInterval(poll);
      var cancelled = false;
      var cancel = function () { cancelled = true; };
      var events = ['wheel', 'touchstart', 'keydown'];
      events.forEach(function (ev) { window.addEventListener(ev, cancel, { passive: true }); });
      var n = 0;
      var restore = setInterval(function () {
        if (cancelled || ++n > 12) {
          clearInterval(restore);
          events.forEach(function (ev) { window.removeEventListener(ev, cancel); });
          return;
        }
        if (Math.abs((window.pageYOffset || 0) - y) > 2) window.scrollTo(0, y);
      }, 40);
    }, 120);
  }

  function esc(id) {
    return (window.CSS && CSS.escape) ? CSS.escape(id) : id;
  }

  function setupBar(bar, realBtn, isProxy) {
    var doc = document.documentElement;
    var sectionId = bar.getAttribute('data-section-id');
    var container = document.getElementById('ProductSection-' + sectionId);
    var myBtn = bar.querySelector('[data-cali-bb-submit]');
    var myLabel = bar.querySelector('[data-cali-bb-label]');
    var myPrice = bar.querySelector('[data-cali-bb-price]');
    var myVariant = bar.querySelector('[data-cali-bb-variant]');
    var myTitle = bar.querySelector('[data-cali-bb-title]');
    var swatchWrap = bar.querySelector('[data-cali-bb-swatches]');
    var optionsWrap = bar.querySelector('[data-cali-bb-options]');
    var secondaryBtn = bar.querySelector('[data-cali-bb-secondary]');
    var secondaryLabel = bar.querySelector('[data-cali-bb-secondary-label]');
    var realLabel = realBtn.querySelector('[data-add-to-cart-text]') || realBtn;
    var handleEl = container.hasAttribute('data-product-handle') ? container : document.querySelector('[data-product-handle]');
    var handle = handleEl ? (handleEl.getAttribute('data-product-handle') || '') : '';
    var currentVariantTitle = function () {
      return myVariant ? (myVariant.textContent || '').trim() : '';
    };
    var realPrice = container.querySelector('[data-product-price]');
    var pageTitle = container.querySelector('.product-single__title');

    /* ---------- swatch clones + option selects ---------- */
    var swatches = [];
    var selects = [];

    function optionInputs() {
      return container.querySelectorAll(
        '.variant-wrapper input[type="radio"], .variant-input-wrap input[type="radio"]'
      );
    }

    function build() {
      if (isProxy) return; /* combo pages have no picker of their own */
      var groups = {};
      var order = [];
      Array.prototype.forEach.call(optionInputs(), function (input) {
        if (!input.name) return;
        var label = input.id ? container.querySelector('label[for="' + esc(input.id) + '"]') : null;
        var shown = label ? label.offsetParent !== null : input.offsetParent !== null;
        if (!shown && !input.checked) return;
        if (!groups[input.name]) { groups[input.name] = []; order.push(input.name); }
        groups[input.name].push({ input: input, label: label });
      });

      order.forEach(function (name) {
        var entries = groups[name];
        if (entries.length < 2) return;

        /* a group whose labels are real swatches is rendered as swatches */
        var isSwatchGroup = entries.every(function (e) {
          return e.label && e.label.classList.contains('color-swatch');
        });

        if (isSwatchGroup && swatchWrap) {
          addGroupLabel(entries[0].label);
          entries.forEach(function (e) {
            var clone = e.label.cloneNode(true);
            clone.className = 'cali-buy-bar__sw';
            /* The theme's swatch label carries its value as text, hidden by
               sibling/parent selectors a clone cannot inherit - strip it so the
               clone is just the fabric image. Any <img> swatch is kept. */
            Array.prototype.slice.call(clone.childNodes).forEach(function (n) {
              if (n.nodeType === 1 && n.tagName === 'IMG') return;
              clone.removeChild(n);
            });
            /* keeps for="" so the click activates the page's own input */
            clone.setAttribute('title', e.input.value);
            clone.setAttribute('aria-label', e.input.value);
            clone.setAttribute('data-cali-cta', 'buy-bar-swatch');
            if (e.input.disabled || e.label.classList.contains('disabled')) {
              clone.classList.add('is-unavailable');
            }
            swatchWrap.appendChild(clone);
            swatches.push({ el: clone, input: e.input });
          });
          return;
        }

        if (!optionsWrap) return;
        var wrap = document.createElement('label');
        wrap.className = 'cali-buy-bar__opt';
        var caption = document.createElement('span');
        caption.className = 'cali-buy-bar__opt-label';
        caption.textContent = name;
        var select = document.createElement('select');
        select.className = 'cali-buy-bar__select';
        select.setAttribute('aria-label', name);
        entries.forEach(function (e) {
          var opt = document.createElement('option');
          opt.value = e.input.value;
          opt.textContent = e.input.value;
          if (e.input.checked) opt.selected = true;
          select.appendChild(opt);
        });
        select.setAttribute('data-cali-cta', 'buy-bar-option');
        select.addEventListener('change', function () {
          track('buy_bar_variant_change', { control: 'select', option: name, value: select.value });
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].input.value === select.value) { entries[i].input.click(); break; }
          }
        });
        wrap.appendChild(caption);
        wrap.appendChild(select);
        optionsWrap.appendChild(wrap);
        selects.push({ select: select, entries: entries });
      });
    }

    /* Mirrors the page's picker labels (CORE / SHIELD ...) so a two-group
       edition picker reads the same in the dock as on the page. */
    function addGroupLabel(sampleLabel) {
      var group = sampleLabel ? sampleLabel.closest('.edition-group') : null;
      if (!group || !swatchWrap) return;
      var labelEl = group.querySelector('.variant__label, legend');
      var text = labelEl ? (labelEl.textContent || '').split(':')[0].trim() : '';
      if (!text) return;
      var chip = document.createElement('span');
      chip.className = 'cali-buy-bar__swgroup';
      chip.textContent = text;
      swatchWrap.appendChild(chip);
    }

    /* The edition picker also offers the sibling edition (SHIELD) as
       cross-product LINKS, not radios - the page shows them, so the dock shows
       them too, and a tap goes to that product exactly as on the page. */
    function addLinkedSwatches() {
      if (!swatchWrap) return;
      var groups = container.querySelectorAll('.edition-group--linked');
      Array.prototype.forEach.call(groups, function (group) {
        var links = group.querySelectorAll('a.color-swatch');
        if (!links.length) return;
        var labelEl = group.querySelector('.variant__label, legend');
        var text = labelEl ? (labelEl.textContent || '').split(':')[0].trim() : '';
        if (text) {
          var chip = document.createElement('span');
          chip.className = 'cali-buy-bar__swgroup';
          chip.textContent = text;
          swatchWrap.appendChild(chip);
        }
        Array.prototype.forEach.call(links, function (a) {
          if (a.offsetParent === null) return;
          var clone = a.cloneNode(true);
          clone.className = 'cali-buy-bar__sw cali-buy-bar__sw--link';
          Array.prototype.slice.call(clone.childNodes).forEach(function (n) {
            if (n.nodeType === 1 && n.tagName === 'IMG') return;
            clone.removeChild(n);
          });
          var t = a.getAttribute('aria-label') || a.getAttribute('title') || '';
          if (t) { clone.setAttribute('title', t); clone.setAttribute('aria-label', t); }
          swatchWrap.appendChild(clone);
        });
      });
    }

    /* Current value per option, read from the variant title ("Colour / Size").
       Deliberately not from the app's own classes: Rio hashes them per build
       (PwzrButtonBase-root-938), so they are not safe to depend on. */
    function currentOptionValues() {
      var title = '';
      var master = container.querySelector('select[data-product-select], select[name="id"]');
      if (master && master.selectedIndex >= 0) {
        title = (master.options[master.selectedIndex].text || '').split(' - ')[0];
      }
      if (!title && myVariant) title = (myVariant.textContent || '').trim();
      return title.split(' / ').map(function (p) { return p.trim(); });
    }

    function syncAppPicker() {
      if (!appGroups.length) return;
      var parts = currentOptionValues();
      appGroups.forEach(function (g, i) {
        /* match by known value first, fall back to positional */
        var values = g.els.length ? g.els.map(function (e) { return e.value; })
          : Array.prototype.map.call(g.select.options, function (o) { return o.value; });
        var val = null;
        for (var p = 0; p < parts.length; p++) if (values.indexOf(parts[p]) !== -1) { val = parts[p]; break; }
        if (val === null) val = parts[i];
        if (val == null) return;
        g.els.forEach(function (e) { e.el.classList.toggle('is-selected', e.value === val); });
        if (g.select && g.select.value !== val) g.select.value = val;
      });
    }

    function syncPicker() {
      syncAppPicker();
      swatches.forEach(function (s) {
        s.el.classList.toggle('is-selected', s.input.checked);
      });
      selects.forEach(function (entry) {
        for (var i = 0; i < entry.entries.length; i++) {
          if (entry.entries[i].input.checked && entry.select.value !== entry.entries[i].input.value) {
            entry.select.value = entry.entries[i].input.value;
            break;
          }
        }
      });
    }

    /* ---------- app-rendered pickers (Rio / Variant Wizard) ----------
       On these products the theme's own picker is hidden and the app draws its
       own, so there are no theme radios to mirror. The app's controls DO drive
       the real form (verified: clicking one updates the master select, the
       price and the URL), so the dock mirrors those controls and forwards
       clicks to them - same rule as everywhere else: never reimplement, always
       drive the page's own UI. Selection is read back from the variant title
       rather than the app's internal classes, which are hashed per build. */
    var appGroups = [];

    function appRoot() {
      return container.querySelector('.rio-options') || document.querySelector('.rio-options');
    }

    function buildAppOptions() {
      var root = appRoot();
      if (!root || appGroups.length) return false;

      /* Only mirror groups the app itself names in its visible titles
         ("Colour", "Size", ...). Rio also exposes a cross-product switcher
         whose aria-label is an internal UUID and whose options are OTHER
         PRODUCTS (the tee's Men/Women "Gender") - mirroring that would put a
         uuid-labelled control in the dock, so it is skipped by design. */
      var allowed = {};
      Array.prototype.forEach.call(root.querySelectorAll('.rio-product-option-title'), function (t) {
        var n = (t.textContent || '').split(':')[0].trim();
        if (n) allowed[n.toLowerCase()] = n;
      });

      var groupEls = [];
      var seen = {};
      Array.prototype.forEach.call(root.querySelectorAll('[aria-label]'), function (el) {
        if (el.offsetParent === null) return;
        var name = (el.getAttribute('aria-label') || '').trim();
        if (!name || !allowed[name.toLowerCase()]) return;
        var n = el.querySelectorAll('[data-option-value]').length;
        if (n < 2) return;
        var key = name.toLowerCase();
        /* keep the innermost element that still holds the whole set */
        if (seen[key]) {
          if (seen[key].el.contains(el) && el.querySelectorAll('[data-option-value]').length === seen[key].n) {
            groupEls[groupEls.indexOf(seen[key].el)] = el;
            seen[key] = { el: el, n: n };
          }
          return;
        }
        seen[key] = { el: el, n: n };
        groupEls.push(el);
      });

      groupEls.forEach(function (groupEl) {
        var name = groupEl.getAttribute('aria-label') || '';
        var opts = Array.prototype.slice.call(groupEl.querySelectorAll('[data-option-value]'))
          .filter(function (o) { return o.offsetParent !== null; });
        if (opts.length < 2) return;

        /* a group whose options carry a colour/image and no text is a swatch row */
        var isSwatch = opts.every(function (o) {
          var cs = getComputedStyle(o);
          var hasPaint = (cs.backgroundImage && cs.backgroundImage !== 'none') ||
            (cs.backgroundColor && cs.backgroundColor !== 'rgba(0, 0, 0, 0)');
          return hasPaint && !(o.textContent || '').trim();
        });

        var entry = { name: name, opts: opts, isSwatch: isSwatch, els: [], select: null };

        if (isSwatch && swatchWrap) {
          if (groupEls.length > 1 && name) {
            var chip = document.createElement('span');
            chip.className = 'cali-buy-bar__swgroup';
            chip.textContent = name;
            swatchWrap.appendChild(chip);
          }
          opts.forEach(function (o) {
            var cs = getComputedStyle(o);
            var el = document.createElement('button');
            el.type = 'button';
            el.className = 'cali-buy-bar__sw';
            el.setAttribute('data-cali-cta', 'buy-bar-swatch');
            var v = o.getAttribute('data-option-value') || o.getAttribute('aria-label') || '';
            el.setAttribute('title', v);
            el.setAttribute('aria-label', v);
            el.style.backgroundColor = cs.backgroundColor;
            if (cs.backgroundImage && cs.backgroundImage !== 'none') el.style.backgroundImage = cs.backgroundImage;
            if (o.getAttribute('aria-disabled') === 'true') el.classList.add('is-unavailable');
            el.addEventListener('click', function () {
              track('buy_bar_variant_change', { control: 'swatch_app', option: name, value: v });
              o.click();
            });
            swatchWrap.appendChild(el);
            entry.els.push({ el: el, value: v });
          });
        } else if (optionsWrap) {
          var wrap = document.createElement('label');
          wrap.className = 'cali-buy-bar__opt';
          var caption = document.createElement('span');
          caption.className = 'cali-buy-bar__opt-label';
          caption.textContent = name;
          var select = document.createElement('select');
          select.className = 'cali-buy-bar__select';
          select.setAttribute('aria-label', name);
          select.setAttribute('data-cali-cta', 'buy-bar-option');
          opts.forEach(function (o) {
            var v = o.getAttribute('data-option-value') || (o.textContent || '').trim();
            var opt = document.createElement('option');
            opt.value = v;
            opt.textContent = v;
            select.appendChild(opt);
          });
          select.addEventListener('change', function () {
            track('buy_bar_variant_change', { control: 'select_app', option: name, value: select.value });
            for (var i = 0; i < opts.length; i++) {
              var v = opts[i].getAttribute('data-option-value') || (opts[i].textContent || '').trim();
              if (v === select.value) { opts[i].click(); break; }
            }
          });
          wrap.appendChild(caption);
          wrap.appendChild(select);
          optionsWrap.appendChild(wrap);
          entry.select = select;
        }
        appGroups.push(entry);
      });

      if (appGroups.length) { syncPicker(); setHeightVar(); return true; }
      return false;
    }

    build();
    addLinkedSwatches();
    if (!selects.length && !swatches.length) {
      /* the app mounts asynchronously - build as soon as its picker exists */
      if (!buildAppOptions()) {
        var appMo = new MutationObserver(function () { if (buildAppOptions()) appMo.disconnect(); });
        appMo.observe(document.body, { childList: true, subtree: true });
        setTimeout(function () { appMo.disconnect(); }, 30000);
      }
    }
    syncPicker();

    if (swatchWrap) {
      swatchWrap.addEventListener('click', function (e) {
        var sw = e.target.closest('.cali-buy-bar__sw');
        if (!sw) return;
        track('buy_bar_variant_change', {
          control: sw.classList.contains('cali-buy-bar__sw--link') ? 'swatch_linked' : 'swatch',
          value: sw.getAttribute('aria-label') || ''
        });
      });
    }

    /* Fade the ends of the swatch row only while it actually scrolls, and drop
       the fade on whichever end is already reached, so nothing is dimmed
       without reason. */
    function syncSwatchFade() {
      if (!swatchWrap) return;
      var scrollable = swatchWrap.scrollWidth - swatchWrap.clientWidth > 2;
      swatchWrap.classList.toggle('is-scrollable', scrollable);
      if (!scrollable) {
        swatchWrap.classList.remove('is-at-start', 'is-at-end');
        return;
      }
      var x = swatchWrap.scrollLeft;
      swatchWrap.classList.toggle('is-at-start', x <= 2);
      swatchWrap.classList.toggle('is-at-end', x >= swatchWrap.scrollWidth - swatchWrap.clientWidth - 2);
    }
    if (swatchWrap) {
      swatchWrap.addEventListener('scroll', syncSwatchFade, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(syncSwatchFade).observe(swatchWrap);
      syncSwatchFade();
    }

    /* ---------- secondary CTA, mirrored from the page ---------- */
    var realSecondary = null;
    var scope = isProxy ? (container.querySelector('#react-customization-app') || container) : container;
    var candidates = scope.querySelectorAll('.add-to-cart--secondary, [data-cali-strap-cta] button, .cali-strap-cta__btn');
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i] !== realBtn && candidates[i].offsetParent !== null) {
        realSecondary = candidates[i];
        break;
      }
    }
    var forwarding = false; /* set while the dock drives a page button */

    if (realSecondary && secondaryBtn) {
      var stext = (realSecondary.textContent || '').trim();
      if (stext) {
        secondaryLabel.textContent = stext;
        secondaryBtn.removeAttribute('hidden');
        secondaryBtn.addEventListener('click', function () {
          track('pdp_cta_click', {
            placement: 'buy_bar', cta: 'secondary', label: stext,
            product_handle: handle, variant: currentVariantTitle()
          });
          preserveScrollAcrossBuilder();
          forwarding = true;
          realSecondary.click();
          forwarding = false;
        });
      }
    }

    /* The page's OWN buttons report too, so the two placements are comparable
       and a failure in either is visible. The forwarding flag stops a dock
       click (which really does click the page button in proxy mode) from
       being counted twice. */
    realBtn.addEventListener('click', function () {
      if (forwarding) return;
      track('pdp_cta_click', {
        placement: 'inline', cta: 'add_to_cart',
        product_handle: handle, variant: currentVariantTitle()
      });
    });

    if (realSecondary) {
      realSecondary.addEventListener('click', function () {
        if (!forwarding) {
          track('pdp_cta_click', {
            placement: 'inline', cta: 'secondary',
            product_handle: handle, variant: currentVariantTitle()
          });
        }
        /* same builder scroll reset happens from the page's own button */
        preserveScrollAcrossBuilder();
      });
    }

    /* ---------- visible / title state ----------
       (realSecondary is resolved above, so apply() can test both CTAs) */
    var visible = null;
    var titleless = null;
    var priceless = null;

    function setHeightVar() {
      doc.style.setProperty('--cali-bb-h', Math.round(bar.getBoundingClientRect().height) + 'px');
    }

    /* Shopify's draft-theme preview bar is fixed to the bottom and would cover
       the dock during review. Absent on a published theme, so inert on live. */
    function setPreviewOffset() {
      var pb = document.getElementById('PBarNextFrameWrapper') ||
        document.getElementById('preview-bar-iframe');
      var h = pb ? Math.round(pb.getBoundingClientRect().height) : 0;
      if (!(h > 0 && h < 200)) h = 0;
      doc.style.setProperty('--cali-bb-offset', h + 'px');
    }

    function apply() {
      var vpH = window.innerHeight || doc.clientHeight || 0;

      /* Never show the dock's call to action while the page's own one is on
         screen (Ali 2026-08-20) - two identical CTAs at once is incoherent.
         The usable area excludes whatever covers the bottom of the viewport:
         the consent bar, Shopify's draft preview bar, and - only while the
         dock is already up - the dock itself. Measuring the dock only in that
         state is deliberate hysteresis: it stops the two from fighting at the
         boundary. */
      var reserve = 0;
      var consent = document.querySelector('.cali-consent:not([hidden])');
      if (consent) reserve += consent.offsetHeight;
      var pbOffset = parseInt(getComputedStyle(doc).getPropertyValue('--cali-bb-offset'), 10);
      if (pbOffset > 0) reserve += pbOffset;
      if (visible) reserve += bar.offsetHeight;

      /* ANY visible part of a page CTA is enough to stand the dock down - a
         half-visible Add to cart next to the dock's own is exactly the
         duplication we are avoiding. Both the primary and the page's secondary
         CTA count. */
      var usableBottom = vpH - reserve;
      var intersects = function (el) {
        if (!el || el.offsetParent === null) return false;
        var rect = el.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < usableBottom;
      };
      var wantVisible = !(intersects(realBtn) || intersects(realSecondary));

      if (wantVisible !== visible) {
        visible = wantVisible;
        bar.classList.toggle('is-visible', visible);
        doc.classList.toggle('cali-bb-active', visible);
      }

      /* Never repeat what the page is already showing. Both tests use the
         area the dock does not cover, so an element hidden BEHIND the dock
         still counts as off screen. */
      var usableH = vpH - (bar.offsetHeight || 0);

      var t = pageTitle ? pageTitle.getBoundingClientRect() : null;
      var titleOnScreen = !!t && t.bottom > 0 && t.top < usableH;
      if (titleOnScreen !== titleless) {
        titleless = titleOnScreen;
        bar.classList.toggle('is-titleless', titleOnScreen);
      }

      var p = realPrice ? realPrice.getBoundingClientRect() : null;
      var priceOnScreen = !!p && p.bottom > 0 && p.top < usableH;
      if (priceOnScreen !== priceless) {
        priceless = priceOnScreen;
        bar.classList.toggle('is-priceless', priceOnScreen);
      }

      setPreviewOffset();
      setHeightVar();
    }

    /* Timestamp throttle, not requestAnimationFrame: rAF stalls in hidden tabs. */
    var lastRun = 0;
    var timer = null;
    function onScroll() {
      var now = Date.now();
      if (now - lastRun > 80) {
        lastRun = now;
        apply();
      } else {
        clearTimeout(timer);
        timer = setTimeout(function () { lastRun = Date.now(); apply(); }, 80);
      }
    }

    apply(); /* synchronous: correct on the very first paint */
    setTimeout(apply, 1200); /* re-measure once the preview bar (if any) exists */

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('orientationchange', onScroll);
    window.addEventListener('pageshow', apply);
    /* Images and app-rendered pickers change the buy area's position after
       first paint; re-decide once everything has loaded so the dock cannot
       linger next to a page CTA that has just come into view. */
    if (document.readyState !== 'complete') window.addEventListener('load', apply);

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { setHeightVar(); }).observe(bar);
      /* Re-evaluate when the PAGE reflows, not only on scroll: the Bag Builder
         app mounts late and shifts the buy area, which could otherwise leave
         the dock up for a moment beside a now-visible page CTA until the next
         scroll event. */
      new ResizeObserver(function () { apply(); }).observe(container);
    }

    /* ---------- mirrors ---------- */
    var syncBtn = function () {
      var t = realLabel.textContent.trim();
      if (t && myLabel && myLabel.textContent.trim() !== t) myLabel.textContent = t;
      myBtn.disabled = realBtn.disabled;
      myBtn.classList.toggle('btn--loading', realBtn.classList.contains('btn--loading'));
      myBtn.classList.toggle('disabled', realBtn.classList.contains('disabled'));
    };
    new MutationObserver(syncBtn).observe(realBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      childList: true,
      subtree: true,
      characterData: true
    });
    syncBtn();

    myBtn.addEventListener('click', function () {
      if (myBtn.disabled) return;
      track('pdp_cta_click', {
        placement: 'buy_bar', cta: 'add_to_cart',
        product_handle: handle, variant: currentVariantTitle()
      });
    });

    if (isProxy) {
      myBtn.addEventListener('click', function () {
        if (myBtn.disabled) return;
        /* On the builder pages this button opens the wizard, which resets the
           page scroll when it closes; no-ops when no dialog appears. */
        preserveScrollAcrossBuilder();
        forwarding = true;
        realBtn.click();
        forwarding = false;
      });
    }

    if (realPrice && myPrice) {
      var syncPrice = function () {
        var t = realPrice.textContent.trim();
        if (t && myPrice.textContent.trim() !== t) myPrice.textContent = t;
        myPrice.classList.toggle('sale-price', realPrice.classList.contains('sale-price'));
      };
      new MutationObserver(syncPrice).observe(realPrice, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class']
      });
      syncPrice();
    }

    container.addEventListener('variantChange', function (evt) {
      var v = evt.detail && evt.detail.variant;
      if (v && v.title && myVariant) myVariant.textContent = v.title;
      syncPicker();
      setHeightVar();
    });

    /* App pickers drive the master select directly and may never fire the
       theme's variantChange, so watch the select itself: it is the one thing
       every picker on this store agrees on. */
    var masterSel = container.querySelector('select[data-product-select], select[name="id"]');
    if (masterSel) {
      var reflectMaster = function () {
        if (masterSel.selectedIndex < 0) return;
        var t = (masterSel.options[masterSel.selectedIndex].text || '').split(' - ')[0].trim();
        if (t && myVariant && myVariant.textContent.trim() !== t) myVariant.textContent = t;
        syncPicker();
      };
      masterSel.addEventListener('change', reflectMaster);
      new MutationObserver(reflectMaster).observe(masterSel, { attributes: true, childList: true, subtree: true });
    }

    if (myTitle) {
      myTitle.addEventListener('click', function () {
        var target = container.querySelector('.variant-wrapper') ||
          container.querySelector('[data-product-blocks]') || realBtn;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  function initBar(bar) {
    if (bar.dataset.caliBbReady === '1') return;

    var sectionId = bar.getAttribute('data-section-id');
    var container = document.getElementById('ProductSection-' + sectionId);
    var myBtn = bar.querySelector('[data-cali-bb-submit]');
    var isProxy = bar.getAttribute('data-cali-bb-mode') === 'proxy';

    if (!container || !myBtn) {
      if (bar.parentNode) bar.parentNode.removeChild(bar);
      return;
    }

    if (!isProxy) {
      /* Always getAttribute('id'), never form.id: the product form contains
         <select name="id">, which shadows the id property on the element. */
      var form = document.getElementById(myBtn.getAttribute('form'));
      var realBtn = container.querySelector('[data-add-to-cart]');
      if (!form || !realBtn) {
        if (bar.parentNode) bar.parentNode.removeChild(bar);
        return;
      }
      bar.dataset.caliBbReady = '1';
      document.documentElement.classList.add('cali-bb-init');
      setupBar(bar, realBtn, false);
      return;
    }

    /* proxy mode: wait for the React app's own add button to mount */
    bar.dataset.caliBbReady = '1';
    document.documentElement.classList.add('cali-bb-init');
    /* Prefer the combo pages' real add-to-cart; on the full builder pages that
       button does not exist and the app's primary control is "Design yours",
       which opens the wizard. Either way the dock forwards to the app's own
       button - it never adds the virtual master itself. */
    var findAppBtn = function () {
      var app = container.querySelector('#react-customization-app') ||
        document.querySelector('#react-customization-app');
      if (!app) return null;
      var mapping = app.querySelector('.mapping-add-to-cart');
      if (mapping && mapping.offsetParent !== null) return mapping;
      var buttons = app.querySelectorAll('.add-to-cart');
      for (var i = 0; i < buttons.length; i++) {
        if (buttons[i].offsetParent === null) continue;
        if (buttons[i].classList.contains('add-to-cart--secondary')) continue;
        return buttons[i];
      }
      return null;
    };
    var appBtn = findAppBtn();
    if (appBtn) { setupBar(bar, appBtn, true); return; }
    var mo = new MutationObserver(function () {
      var b = findAppBtn();
      if (b) { mo.disconnect(); setupBar(bar, b, true); }
    });
    mo.observe(container, { childList: true, subtree: true });
    setTimeout(function () { mo.disconnect(); }, 30000);
  }

  function initAll() {
    document.querySelectorAll('[data-cali-buy-bar]').forEach(initBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
